import { useTranslation } from "react-i18next";
import { styled, InputAdornment, MenuItem, IconButton } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { AccountingDetailsFilterRequest } from "@/models/accounting";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import { OrderType, AccountingDocType } from "@/models/enums";
import { IdText } from "@/models/apiModels";
import { useEffect, useState } from "react";
import ClearIcon from "@/assets/icons/clear-icon.svg";

import SearchIcon from "@mui/icons-material/Search";

interface AccountingDetailsFilterProps {
    searchClick: Function;
    filter: AccountingDetailsFilterRequest;
    setFilter: Function;
    docType: AccountingDocType;
}

const SearchButton = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
            padding: 0px 20px;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
            padding: 0px 20px;
          }

          &.${buttonUnstyledClasses.root} {
            padding: 0px 20px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
            padding: 0px 20px;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
            padding: 0px 20px;
          }`
);

function AccountingDetailsFilter({ searchClick, filter, setFilter, docType }: AccountingDetailsFilterProps) {
    const { t } = useTranslation();
    const [counter, setCounter] = useState(0);

    function handleSubmit(event: any) {
        event.preventDefault();
        searchClick();
    }

    useEffect(() => {
        searchClick();
    }, [counter]);

    const clearFilter = () => {
        setFilter(() => new AccountingDetailsFilterRequest());
        setCounter(counter + 1);
    };

    const orderTypeList: IdText[] = (
        Object.values(OrderType).filter((val) => isNaN(Number(val))) as (keyof typeof OrderType)[]
    ).map((key) => new IdText(OrderType[key].toString(), key));

    return (
        <div id="accounting-filter-block" className="accounting-filter-block">
            <div className="accounting-filter">
                <form onSubmit={handleSubmit}>
                    <div className="font-style accounting-span">{t("accounting.FilterHeader")}</div>
                    <div className="accounting-filter-first-row">
                        <div className="accounting-filter-fields">
                            <div>
                                <BaseInput
                                    id="keyword-input"
                                    className="search-input"
                                    placeholder={`${t("accounting.FilterSearch")}`}
                                    type="search"
                                    size="small"
                                    value={filter.keywords}
                                    onChange={(event) =>
                                        setFilter(() => ({
                                            ...filter,
                                            keywords: event.target.value,
                                        }))
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        width: "397px",
                                    }}
                                />
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("accounting.fromDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.fromDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                fromDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("accounting.endDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.toDate}
                                        minDate={filter.fromDate ?? undefined}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                toDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            {docType === AccountingDocType.Invoice ? (
                                <div>
                                    <BaseInput
                                        select
                                        label={t("orders.orderType")}
                                        size="small"
                                        value={filter.orderType || ""}
                                        onChange={(event) =>
                                            setFilter(() => ({
                                                ...filter,
                                                orderType: event.target.value,
                                            }))
                                        }
                                        sx={{
                                            width: "250px",
                                            fontSize: 14,
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    className="select-clear-icon"
                                                    sx={{
                                                        visibility: filter.orderType ? "visible" : "hidden",
                                                    }}
                                                    onClick={() =>
                                                        setFilter(() => ({
                                                            ...filter,
                                                            orderType: null,
                                                        }))
                                                    }
                                                >
                                                    <img src={ClearIcon} alt="Clear icon" />
                                                </IconButton>
                                            ),
                                        }}
                                    >
                                        {orderTypeList.map((orderType) => (
                                            <MenuItem key={orderType.id} value={orderType.text}>
                                                {t(`enums.OrderType.${orderType.text}`)}
                                            </MenuItem>
                                        ))}
                                    </BaseInput>
                                </div>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="filter-search-btn">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <span onClick={() => clearFilter()} className="link-element clear-all-btn">
                                    {t("display.ClearAll")}
                                </span>
                            </div>
                            <SearchButton className="basic-button" type="submit">
                                {t("general.filter")}
                            </SearchButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AccountingDetailsFilter;
